// Type-to-filter for a long list of checkboxes — the table column picker,
// which runs to ~60 entries across several groups.
//
// Deliberately client-side: the list is already fully in the DOM and the
// server has nothing to add, so a round trip per keystroke would only add
// latency. Nothing here touches the checkboxes' state, only whether their
// row is displayed, so filtering can never change what is selected.
//
// The hook goes on the *list container*, not on the input. Toggling a column
// re-renders the list from the server, which arrives without our `hidden`
// classes — `updated()` has to fire to reapply the query, and it only fires
// for the element the hook is attached to. The input carries
// `phx-update="ignore"` so the same patch leaves the typed query alone.
//
// Structure it reads, all within the container:
//   [data-filter-input] — the text input
//   [data-filter-item]  — each filterable row
//   [data-filter-group] — group headings, hidden when their group is empty
//   [data-filter-empty] — shown when nothing matches
const HIDDEN = "hidden";

export const ListFilter = {
  mounted() {
    this.onInput = (event) => {
      if (event.target.matches("[data-filter-input]")) this.apply();
    };
    this.el.addEventListener("input", this.onInput);

    // Escape clears rather than closing the surrounding dropdown, which is
    // what you want mid-filter; a second Escape hits the dropdown as usual.
    this.onKeydown = (event) => {
      if (!event.target.matches("[data-filter-input]")) return;

      if (event.key === "Escape" && event.target.value !== "") {
        event.stopPropagation();
        event.target.value = "";
        this.apply();
      }
    };
    this.el.addEventListener("keydown", this.onKeydown);
  },

  updated() {
    this.apply();
  },

  destroyed() {
    this.el.removeEventListener("input", this.onInput);
    this.el.removeEventListener("keydown", this.onKeydown);
  },

  apply() {
    const input = this.el.querySelector("[data-filter-input]");
    if (!input) return;

    const query = input.value.trim().toLowerCase();
    const items = Array.from(this.el.querySelectorAll("[data-filter-item]"));

    for (const item of items) {
      const text = (item.textContent || "").toLowerCase();
      item.classList.toggle(HIDDEN, query !== "" && !text.includes(query));
    }

    // A heading with nothing under it reads as an empty section, so hide it.
    // Walking forward from each heading to the next keeps this working without
    // depending on how the groups are nested.
    for (const group of this.el.querySelectorAll("[data-filter-group]")) {
      let sibling = group.nextElementSibling;
      let anyVisible = false;

      while (sibling && !sibling.matches("[data-filter-group]")) {
        if (sibling.matches("[data-filter-item]") && !sibling.classList.contains(HIDDEN)) {
          anyVisible = true;
          break;
        }
        sibling = sibling.nextElementSibling;
      }

      group.classList.toggle(HIDDEN, !anyVisible);
    }

    const empty = this.el.querySelector("[data-filter-empty]");
    if (empty) {
      empty.classList.toggle(
        HIDDEN,
        items.some((item) => !item.classList.contains(HIDDEN)),
      );
    }
  },
};
